import { createContext, useContext, useRef, useState } from 'react';
import { useError } from './ErrorContext';

interface MarkerContextType {
  registerMarker: (id: string, ref: any) => void;
  getMarker: (id: string) => any;
  updateMarker: (id: string, props: any) => void;
  markerOnTopId: string | null;
  setMarkerOnTopId: (id: string | ((prev: string | null) => string | null)) => void;
}

const MarkerContext = createContext<MarkerContextType | null>(null);

const MarkerProvider = ({ children }: { children: React.ReactNode }) => {
  const markerRefs = useRef<Record<string, any>>({});
  const [markerOnTopId, setMarkerOnTopId] = useState<string | null>(null);
  const { addError } = useError();

  const registerMarker = (id: string, ref: any) => {
    (markerRefs.current as Record<string, any>)[id] = ref;
  };

  const getMarker = (id: string) => (markerRefs.current as Record<string, any>)[id];
  const updateMarker = (id: string, props: any) => {
    try {
      const marker = getMarker(id);
      if (marker) {
        if ('icon' in props) { marker.setIcon(props.icon); }
        if ('position' in props) { marker.setPosition(props.position); }
        if ('popup' in props) { marker.setPopupContent(props.popup); }
        if ('zIndexOffset' in props) { marker.setZIndexOffset(props.zIndexOffset); }
      }
    } catch (error) {
      console.error('Failed to update marker:', error);
      addError({
        message: 'Failed to update map marker. The map may not display correctly.',
        type: 'warning'
      });
    }
  };

  return (
    <MarkerContext.Provider value={{ registerMarker, getMarker, updateMarker, markerOnTopId, setMarkerOnTopId }}>
      {children}
    </MarkerContext.Provider>
  );
};

const useMarkerContext = () => {
  const context = useContext(MarkerContext);
  if (!context) {
    throw new Error('useMarkerContext must be used within a MarkerProvider');
  }
  return context;
};

MarkerProvider.useMarkerContext = useMarkerContext;

export { MarkerProvider };
